/**
 * Hartenjagen's view of the table.
 *
 * The counterpart of `BoerenbridgeTable`: same `TrickTable` underneath, a
 * different mapping on top. Under each name goes the penalty taken so far
 * rather than a bid, and the corner says which way the cards are being passed
 * instead of what is trump — there is no trump.
 */

import type { HeartsGameState } from '@shared/types/hearts';
import { heartsRulesFor } from '@shared/game-logic';
import TrickTable, { type TableView } from '@/components/TrickTable';

const DIRECTION_LABEL: Record<string, string> = {
  left: 'links',
  right: 'rechts',
  across: 'overkant',
  none: 'niets doorgeven',
};

export default function HeartsTable({
  state,
  mySeat,
}: {
  state: HeartsGameState;
  mySeat: number | null;
}) {
  const passing = state.phase === 'passing';

  /*
   * How much of this hand is still out there.
   *
   * The number that decides everything and is nowhere on the screen: with four of
   * twenty-six gone the queen is still walking around, and with twenty-five gone
   * somebody is one card away from shooting the moon. Both variants are counted
   * from their own rules, so the total is fifteen at a Dutch table.
   */
  const rules = heartsRulesFor(state.variant);
  const taken = state.players.reduce((sum, player) => sum + player.handPoints, 0);
  const left = rules.total - taken;

  const view: TableView = {
    players: state.players.map((player) => ({
      seat: player.seat,
      name: player.name,
      avatar: player.avatar,
      emoji: player.emoji,
      isConnected: player.isConnected,
      handCount: player.handCount,
      detail: passing ? (
        <span className={player.hasPassed ? 'text-good' : 'text-slate-500'}>
          {player.hasPassed ? 'gekozen' : 'kiest…'}
        </span>
      ) : player.handPoints > 0 ? (
        // Only when there is something to show: a row of zeroes round the table
        // is noise, and the number appearing is itself the news.
        <span key={player.handPoints} className="animate-pop-in text-bad">
          +{player.handPoints}
        </span>
      ) : undefined,
      score: (
        <span className="text-xs font-semibold tabular-nums text-slate-300">{player.total}</span>
      ),
      spoken: `${player.name}, ${player.handPoints} strafpunten deze ronde, ${player.total} in totaal`,
    })),
    currentTrick: state.currentTrick,
    lastTrick: state.lastTrick,
    turnSeat: state.turnSeat,
    scoring: state.phase === 'scoring',
    emptyLabel: passing ? 'kaarten kiezen…' : 'wie komt uit?',
    note:
      state.phase === 'playing' || state.phase === 'scoring' ? (
        <span className={left === 0 ? 'text-slate-400' : ''}>
          nog {left} van de {rules.total}
        </span>
      ) : undefined,
    spokenHeader: 'Hartenjagen, geen troef.',
    corner: (
      <span className="rounded-full bg-base-900/80 px-2.5 py-1 text-xs text-slate-300 backdrop-blur">
        {state.passDirection === 'none'
          ? 'niets doorgeven'
          : `doorgeven: ${DIRECTION_LABEL[state.passDirection]}`}
      </span>
    ),
  };

  return <TrickTable view={view} mySeat={mySeat} />;
}
